import { Controller } from '@hotwired/stimulus';
import EmblaCarousel from 'embla-carousel';
import { setupDotBtns, generateDotBtns, selectDotBtn } from './dotButtons';
import { disablePrevNextBtns } from './prevAndNextButtons';

export default class extends Controller {
    static targets = ['viewport', 'dots', 'prevButton', 'nextButton'];
    static values = { settings: Object };

    carousel = null;

    connect() {

        this.optionsDefault = {
            loop: false,
            align: 'start',
            speed: 20,
            ...this.settingsValue,
        };

        this.element.classList.add('is-initialized');
        this.carousel = EmblaCarousel(this.viewportTarget, this.optionsDefault);
        this.slides = this.carousel.slideNodes();

        // auto-set active/disabled btns
        if (this.hasPrevButtonTarget && this.hasNextButtonTarget) {
            const disablePrevAndNextBtns = disablePrevNextBtns(this.prevButtonTarget, this.nextButtonTarget, this.carousel);

            this.carousel.on('select', disablePrevAndNextBtns);
            this.carousel.on('init', () => {
                disablePrevAndNextBtns();
                this.slides[0]?.classList.add('is-selected');
            });
        }

        // Dots
        if (this.hasDotsTarget) {
            const dotsArray = generateDotBtns(this.dotsTarget, this.carousel);
            const setSelectedDotBtn = selectDotBtn(dotsArray, this.carousel);

            setupDotBtns(dotsArray, this.carousel);

            this.carousel.on('select', setSelectedDotBtn);
            this.carousel.on('init', setSelectedDotBtn);
        }

        // helper class for pointer-events: none when dragging
        const onScroll = () => this.viewportTarget.classList.add('is-dragging');
        this.carousel.on('pointerDown', () => this.carousel.on('scroll', onScroll));
        this.carousel.on('pointerUp', () => {
            this.carousel.off('scroll', onScroll);
            this.viewportTarget.classList.remove('is-dragging');
        });

        const { runNecessary, dispatch, carousel, element } = this;
        this.carousel.on('select', () => {
            runNecessary();
            this.dispatch('emblaChange', { detail: { key: carousel.selectedScrollSnap(), context: [element] } });
        });
        this.carousel.on('init', this.runNecessary);
        this.carousel.on('scroll', this.runNecessary);
        this.carousel.on('resize', this.runNecessary);
    }

    runNecessary = () => {
        // grouped functions, which are triggered all the time :-)
        this.setActiveSlides();
        this.toggleEmblaReady();
    };

    toggleEmblaReady = (event) => {
        const isResizeEvent = event === 'resize';
        const toggleClass = isResizeEvent ? 'remove' : 'add';
        this.element.classList[toggleClass]('embla--is-ready');
        if (isResizeEvent) this.carousel.reInit();
    };

    setActiveSlides = () => {
        this.slides.forEach((item) => item.classList.remove('is-selected'));
        this.carousel.slidesInView().forEach((index) => this.slides[index].classList.add('is-selected'));
    };

    prev(event) {
        event.preventDefault();
        this.carousel.scrollPrev();
    }

    next(event) {
        event.preventDefault();
        this.carousel.scrollNext();
    }
}
